import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Spinner from "../../../components/spinner";
import useRead from "../../../hooks/useRead";
import { convertToSentenceCase } from "../../../utilities";
import Errors from "../../errors";
import ApproveRejectModal from "../../../components/business-applications/ApproveRejectModal";

const BusinessApplicationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  // Fetch single business application
  const { data } = useRead({
    method: "GET",
    url: `business/view`,
    initialData: {
      business_id: id,
    },
  });

  const application = data?.data?.data || {};
  const isLoading = data?.isLoading || false;
  const error = data?.error || null;
  const status = data?.status || null;
  console.log(application);

  const handleApprove = () => {
    setSelectedApplication(application);
    setShowApproveModal(true);
  };

  const handleReject = () => {
    setSelectedApplication(application);
    setShowRejectModal(true);
  };

  if (isLoading) {
    return (
      <div className="container_xxl bg-[#eceff1] px-4">
        <div className="flex justify-center items-center h-64">
          <Spinner />
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="container_xxl bg-[#eceff1] px-4">
        <Errors errorObject={error} />
      </div>
    );
  }

  const {
    first_name,
    last_name,
    business_name,
    business_email,
    business_phone,
    business_category,
    business_address1,
    business_address2,
    business_city,
    business_state,
    business_pincode,
    business_country,
    business_website,
    business_description,
    gst_number,
    pan_number,
    is_email_verified,
    approval_status,
    created_at,
    updated_at,
    business_documents,
    gst_no,
  } = application;

  return (
    <>
      {showApproveModal && (
        <ApproveRejectModal
          isApprove={true}
          isOpen={showApproveModal}
          handleClose={() => setShowApproveModal(false)}
          application={selectedApplication}
          refreshUrl="business/view"
        />
      )}
      {showRejectModal && (
        <ApproveRejectModal
          isApprove={false}
          isOpen={showRejectModal}
          handleClose={() => setShowRejectModal(false)}
          application={selectedApplication}
          refreshUrl="business/view"
        />
      )}

      <div className="container_xxl bg-[#eceff1] px-4">
        <div className="flex items-center space-x-4 headingBorder__b sticky top-0 z-10">
          <div>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
          </div>
          <div>
            <h1 className="heading">Business Application Details</h1>
          </div>
        </div>

        <div className="py-8">
          <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
            {/* Header with status and actions */}
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">
                  {business_name}
                </h2>
                <p className="text-gray-600 mt-1">
                  Applied by: {first_name} {last_name}
                </p>
              </div>
              <div className="flex flex-col items-end space-y-2">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    approval_status === "approved"
                      ? "bg-green-100 text-green-800"
                      : approval_status === "rejected"
                      ? "bg-red-100 text-red-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {convertToSentenceCase(approval_status)}
                </span>
                <span
                  className={`px-3 py-1 rounded-full text-sm ${
                    is_email_verified
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  Email {is_email_verified ? "Verified" : "Not Verified"}
                </span>
              </div>
            </div>

            {/* Action buttons */}
            {is_email_verified && (
              <div className="flex flex-wrap gap-4 pt-6 border-t border-gray-200">
                {["rejected", "pending"].includes(approval_status) && (
                  <button
                    onClick={handleApprove}
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-emerald-700 bg-gradient-to-r from-emerald-100 to-emerald-200 hover:from-emerald-200 hover:to-emerald-300 border border-emerald-300 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Approve Application
                  </button>
                )}
                {["approved", "pending"].includes(approval_status) && (
                  <button
                    onClick={handleReject}
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-rose-700 bg-gradient-to-r from-rose-100 to-rose-200 hover:from-rose-200 hover:to-rose-300 border border-rose-300 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:ring-offset-2"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Reject Application
                  </button>
                )}
              </div>
            )}

            {!is_email_verified && (
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-orange-100 to-orange-200 border border-orange-300 rounded-xl shadow-sm">
                <div className="flex-shrink-0">
                  <svg
                    className="w-6 h-6 text-orange-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.863-.833-2.634 0L4.168 15.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-orange-800">
                    Email Verification Required
                  </h4>
                  <p className="text-orange-700 text-sm mt-1">
                    This application cannot be approved or rejected until the
                    email is verified by the user.
                  </p>
                </div>
              </div>
            )}

            {/* Business Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Business Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">
                      Business Name
                    </label>
                    <p className="mt-1 text-gray-900">
                      {business_name || "---"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">
                      Business Email
                    </label>
                    <p className="mt-1 text-gray-900">
                      {business_email || "---"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">
                      Business Phone
                    </label>
                    <p className="mt-1 text-gray-900">
                      {business_phone || "---"}
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">
                      GST Number
                    </label>
                    <p className="mt-1 text-gray-900 font-mono">
                      {gst_no || "---"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">
                      Business Category
                    </label>
                    <p className="mt-1 text-gray-900">
                      {business_category || "---"}
                    </p>
                  </div>
                  {/* <div>
                    <label className="block text-sm font-medium text-gray-600">
                      PAN Number
                    </label>
                    <p className="mt-1 text-gray-900 font-mono">
                      {pan_number || "---"}
                    </p>
                  </div> */}
                  {/* <div>
                    <label className="block text-sm font-medium text-gray-600">
                      Application Date
                    </label>
                    <p className="mt-1 text-gray-900">
                      {created_at
                        ? new Date(created_at).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "---"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">
                      Last Updated
                    </label>
                    <p className="mt-1 text-gray-900">
                      {updated_at
                        ? new Date(updated_at).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "---"}
                    </p>
                  </div> */}
                </div>
              </div>
            </div>

            {/* Business Description */}
            {business_description && (
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Business Description
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 leading-relaxed">
                    {business_description}
                  </p>
                </div>
              </div>
            )}

            {/* Business Address */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Business Address
              </h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-2">
                  {business_address1 && (
                    <p className="text-gray-700">{business_address1}</p>
                  )}
                  {business_address2 && (
                    <p className="text-gray-700">{business_address2}</p>
                  )}
                  <div className="flex space-x-4">
                    {business_city && (
                      <span className="text-gray-700">{business_city}</span>
                    )}
                    {business_state && (
                      <span className="text-gray-700">{business_state}</span>
                    )}
                    {business_pincode && (
                      <span className="text-gray-700">{business_pincode}</span>
                    )}
                  </div>
                  {business_country && (
                    <p className="text-gray-700">{business_country}</p>
                  )}
                </div>
                {!business_address1 &&
                  !business_address2 &&
                  !business_city &&
                  !business_state &&
                  !business_pincode &&
                  !business_country && (
                    <p className="text-gray-500 italic">
                      No address information provided
                    </p>
                  )}
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Contact Person Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Full Name
                  </label>
                  <p className="mt-1 text-gray-900">
                    {first_name} {last_name}
                  </p>
                </div>
              </div>
            </div>

            {/* Documents Section */}
            {business_documents && business_documents.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Uploaded Documents
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {business_documents.map((doc, index) => (
                    <div
                      key={index}
                      className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-800">
                            {doc.document_type || `Document ${index + 1}`}
                          </p>
                          <p className="text-sm text-gray-600">
                            {doc.file_name || "Document"}
                          </p>
                        </div>
                        {doc.document_url && (
                          <a
                            href={doc.document_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm underline"
                          >
                            View
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BusinessApplicationDetail;
